import { apiClient } from './client';
import type { GuestGroup, GuestGroupMember, CreateGuestGroupDto, UpdateGuestGroupDto, GroupSearchResult, GroupMembership } from '@/types/group.types';

export class GroupService {
  /**
   * Get all groups for a celebration
   */
  async getGroups(celebrationId: number): Promise<GuestGroup[]> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/groups`);
    return response.data.data;
  }

  /**
   * Get a single group
   */
  async getGroup(celebrationId: number, groupId: number): Promise<GuestGroup> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/groups/${groupId}`);
    return response.data.data;
  }

  /**
   * Create a new custom group
   */
  async createGroup(celebrationId: number, data: CreateGuestGroupDto): Promise<GuestGroup> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/groups`, data);
    return response.data.data;
  }

  /**
   * Update a custom group
   */
  async updateGroup(celebrationId: number, groupId: number, data: UpdateGuestGroupDto): Promise<GuestGroup> {
    const response = await apiClient.patch(`/celebrations/${celebrationId}/groups/${groupId}`, data);
    return response.data.data;
  }

  /**
   * Delete a custom group
   */
  async deleteGroup(celebrationId: number, groupId: number): Promise<void> {
    await apiClient.delete(`/celebrations/${celebrationId}/groups/${groupId}`);
  }

  /**
   * Get group members
   */
  async getMembers(celebrationId: number, groupId: number): Promise<GuestGroupMember[]> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/groups/${groupId}/members`);
    return response.data.data;
  }

  /**
   * Add guests to a group
   */
  async addMembers(celebrationId: number, groupId: number, guestIds: number[]): Promise<void> {
    await apiClient.post(`/celebrations/${celebrationId}/groups/${groupId}/members`, { guest_ids: guestIds });
  }

  /**
   * Set exact members of a group (replace all)
   */
  async setMembers(celebrationId: number, groupId: number, guestIds: number[]): Promise<void> {
    await apiClient.put(`/celebrations/${celebrationId}/groups/${groupId}/members`, { guest_ids: guestIds });
  }

  /**
   * Remove a guest from a group
   */
  async removeMember(celebrationId: number, groupId: number, guestId: number): Promise<void> {
    await apiClient.delete(`/celebrations/${celebrationId}/groups/${groupId}/members/${guestId}`);
  }

  /**
   * Search guests using group query syntax
   * Example: "#family & #rsvp-accepted"
   */
  async searchByGroups(celebrationId: number, query: string): Promise<GroupSearchResult> {
    const response = await apiClient.post(`/celebrations/${celebrationId}/groups/search`, { query });
    return response.data.data;
  }

  async getMemberships(celebrationId: number): Promise<GroupMembership[]> {
    const response = await apiClient.get(`/celebrations/${celebrationId}/groups/memberships`);
    return response.data.data;
  }
}

export const groupService = new GroupService();
